'use client';

import { useLocale, useTranslations } from 'next-intl';
import { ComponentProps } from 'react';
import { getVenueAvailabilityBannerMessage } from '@/entities/venue/lib/venue-availability-text.utils';
import { VenueAvailabilityKind } from '@/entities/venue/lib/venue-availability.utils';
import { useVenueAvailabilityStore } from '@/entities/venue/model/venue-availability.store';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';

type OutagesProps = ComponentProps<typeof Layout>;

export const OutagesWrapper = ({ children, media, ...props }: OutagesProps) => {
    const isOpen = useVenueAvailabilityStore((s) => s.status.kind === VenueAvailabilityKind.Open);

    if (isOpen) {
        return null;
    }

    return (
        <Layout
            media={{
                ...media,
                initial: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    backgroundColor: 'var(--color-danger)',
                    color: 'var(--color-foreground-light)',
                    padding: '0.5rem 1rem',
                    ...media?.initial,
                },
            }}
            {...props}
        >
            {children}
        </Layout>
    );
};

type OutagesMessageProps = ComponentProps<typeof Layout>;

export const OutagesMessage = (props: OutagesMessageProps) => {
    const status = useVenueAvailabilityStore((s) => s.status);
    const t = useTranslations();
    const locale = useLocale();
    const getLocalizedValue = useGetLocalizedValue();

    const message = getVenueAvailabilityBannerMessage({
        status,
        locale,
        t,
        getLocalizedValue,
    });

    if (!message) {
        return null;
    }

    return (
        <Layout element="span" {...props}>
            {message}
        </Layout>
    );
};

type OutagesIconProps = ComponentProps<typeof Layout>;

export const OutagesIcon = (props: OutagesIconProps) => {
    const kind = useVenueAvailabilityStore((s) => s.status.kind);
    const iconName = kind === VenueAvailabilityKind.Outage ? 'lock' : 'clock';

    return (
        <Layout {...props}>
            <Icon name={iconName} width={24} height={24} />
        </Layout>
    );
};
